import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { LuMoveDown, LuMoveUp } from 'react-icons/lu'
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable
} from 'react-table'
import TableActionBar from './TableActionBar'
import TableFooter from './TableFooter'

export default function Table({ data, columns, tableName, createNewItemURL }) {
  // Filtro personalizado para rango de fechas. Código basado en este ejemplo: https://github.com/TanStack/table/discussions/2453
  function dateBetweenFilterFn(rows, id, filterValues) {
    const sd = filterValues[0] ? new Date(filterValues[0]) : null
    const ed = filterValues[1] ? new Date(filterValues[1]) : null

    // Normaliza las fechas al inicio del día para evitar problemas de comparación
    const normalizeDate = (date) => {
      if (!date) return null
      return new Date(date.getFullYear(), date.getMonth(), date.getDate())
    }

    const nsd = normalizeDate(sd)
    const ned = normalizeDate(ed)

    return rows.filter((r) => {
      const time = normalizeDate(new Date(r.values[id]))
      let result = true
      if (filterValues.length === 0 || (!nsd && !ned)) {
        result = true // No se aplican filtros, devolver todas las filas
      } else if (nsd && !ned) {
        result = time >= sd // Solo fecha de inicio, filtrar por fechas posteriores o iguales
      } else if (!nsd && ned) {
        result = time <= ned // Solo fecha de fin, filtrar por fechas anteriores o iguales
      } else {
        result = time >= nsd && time <= ned // Ambas fechas existen, filtrar entre ellas
      }

      return result
    })
  }

  dateBetweenFilterFn.autoRemove = (val) => !val

  // Configuración de tipos de filtros
  const filterTypes = useMemo(
    () => ({
      dateBetween: dateBetweenFilterFn
    }),
    []
  )

  // Configuración de la tabla con react-table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // Filas de la página actual
    setAllFilters,
    rows, // Todas las filas (filtradas)
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data,
      filterTypes,
      initialState: { pageSize: 25 } // Tamaño inicial de página
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination
  )

  return (
    <>
      <div className="w-full mx-auto">
        {/* Barra de acciones para la tabla */}
        <TableActionBar
          tableName={tableName}
          createNewItemURL={createNewItemURL}
          datos={data}
          rows={rows}
          columns={columns}
          setAllFilters={setAllFilters}
        />

        {/* Tabla: */}
        <div className="flex flex-col mt-4">
          <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden border border-[#c8e6da] dark:border-gray-700 md:rounded-lg shadow-sm">
                <table
                  {...getTableProps()}
                  className="min-w-full divide-y divide-[#e0f0e8] dark:divide-gray-700"
                >
                  {/* Cabecera */}
                  <thead className="bg-[#006F53] dark:bg-gray-800">
                    {headerGroups.map((headerGroup, index) => (
                      <tr key={index} {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((header, index) => {
                          // La siguiente línea de código sirve para solucionar el siguiente error
                          //  Warning: A props object containing a "key" prop is being spread into JSX:
                          //    let props = {key: someKey, role: ..., onClick: ..., className: ..., children: ...};
                          //    <tr {...props} />
                          //  React keys must be passed directly to JSX without using spread:
                          //    let props = {role: ..., onClick: ..., className: ..., children: ...};
                          //    <tr key={someKey} {...props} />
                          //  Error Component Stack
                          const { key, ...headerProps } = header.getHeaderProps(
                            header.getSortByToggleProps({
                              title: header.TitleDescription
                            })
                          )

                          return (
                            <th
                              key={key}
                              scope="col"
                              className="py-3.5 px-4 text-sm font-semibold text-left rtl:text-right text-white dark:text-gray-300 uppercase tracking-wide"
                              {...headerProps}
                            >
                              <div className="flex flex-col justify-between h-full space-y-2">
                                <div
                                  className={`${header?.Filter ? 'flex-row justify-start items-center text-left' : 'justify-center items-center h-full'} flex w-full text-sm select-none`}
                                >
                                  <span className="overflow-hidden whitespace-nowrap text-ellipsis">
                                    {header.render('Header')}
                                  </span>

                                  <div>
                                    {header.isSorted ? (
                                      header.isSortedDesc ? (
                                        <LuMoveDown size={15} />
                                      ) : (
                                        <LuMoveUp size={15} />
                                      )
                                    ) : (
                                      ''
                                    )}
                                  </div>
                                </div>

                                <div>
                                  {header.Filter
                                    ? header.render('Filter')
                                    : null}
                                </div>
                              </div>
                            </th>
                          )
                        })}
                      </tr>
                    ))}
                  </thead>

                  {/* Cuerpo */}
                  {rows?.length > 0 ? (
                    <tbody
                      className="bg-white divide-y divide-[#e0f0e8] dark:divide-gray-700 dark:bg-gray-900"
                      {...getTableBodyProps()}
                    >
                      {page.map((row, index) => {
                        prepareRow(row)

                        // La siguiente línea de código sirve para solucionar el siguiente error
                        //  Warning: A props object containing a "key" prop is being spread into JSX:
                        //    let props = {key: someKey, role: ..., onClick: ..., className: ..., children: ...};
                        //    <tr {...props} />
                        //  React keys must be passed directly to JSX without using spread:
                        //    let props = {role: ..., onClick: ..., className: ..., children: ...};
                        //    <tr key={someKey} {...props} />
                        //  Error Component Stack
                        const { key, ...rowProps } = row.getRowProps()

                        return (
                          <>
                            <tr key={key} {...rowProps} className="hover:bg-[#f0faf6] dark:hover:bg-gray-800 transition-colors duration-100">
                              {row.cells.map((cell, index) => {
                                return (
                                  <td
                                    key={index}
                                    {...cell.getCellProps()}
                                    // className="py-2 overflow-hidden text-sm font-noto whitespace-nowrap text-ellipsis"
                                    className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap"
                                  >
                                    {cell.render('Cell')}
                                  </td>
                                )
                              })}
                            </tr>
                          </>
                        )
                      })}
                    </tbody>
                  ) : (
                    <tbody
                      className="bg-white divide-y divide-[#e0f0e8] dark:divide-gray-700 dark:bg-gray-900"
                      {...getTableBodyProps()}
                    >
                      <tr>
                        <td
                          className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap"
                          colSpan="3"
                        >
                          No s&apos;han trobat registres
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Pie de tabla */}
        <div className="mt-4 sm:flex sm:items-center sm:justify-between">
          <TableFooter
            previousPage={previousPage}
            canPreviousPage={canPreviousPage}
            pageIndex={pageIndex}
            pageOptions={pageOptions}
            nextPage={nextPage}
            canNextPage={canNextPage}
            gotoPage={gotoPage}
            rows={rows}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </div>
      </div>
    </>
  )
}

// Define los tipos de las props que recibe Table
Table.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  tableName: PropTypes.string.isRequired,
  createNewItemURL: PropTypes.string.isRequired
}
